import type { approvedVjalComponents } from "./product-screen-registry";

type ApprovedVjalComponent = (typeof approvedVjalComponents)[number];

interface ScreenComponentProvenance {
  sourcePath: string;
  components: ApprovedVjalComponent[];
}

const sessionInvitesComponents: ApprovedVjalComponent[] = [
  "Alert",
  "Badge",
  "Button",
  "DataTableCollection",
  "Module",
  "ModuleBody",
  "ModuleHead",
  "PageFrame"
];
const sessionRescheduleComponents: ApprovedVjalComponent[] = [
  "Button",
  "DataTableCollection",
  "FormField",
  "Input",
  "Module",
  "ModuleBody",
  "ModuleHead",
  "PageFrame",
  "Select"
];
const userManagementComponents: ApprovedVjalComponent[] = [
  "Alert",
  "Badge",
  "Button",
  "DataTable",
  "FormField",
  "Input",
  "Module",
  "ModuleBody",
  "ModuleHead",
  "Select",
  "Switch"
];
const profileComponents: ApprovedVjalComponent[] = [...userManagementComponents.filter((c) => c !== "DataTable"), "Card", "DataTableV2"];

export const screenComponentProvenance: Record<string, ScreenComponentProvenance[]> = {
  login: [
    {
      sourcePath: "src/pages/product-route-contracts.tsx",
      components: ['Alert', 'AuthSurface', 'Badge', 'Module', 'ModuleBody', 'StatusDot']
    }
  ],
  dashboard: [
    {
      sourcePath: "src/pages/product-dashboard-surface.tsx",
      components: ['Alert', 'Badge', 'DataTableCollection', 'Kpi', 'Module', 'ModuleBody', 'ModuleHead', 'PageFrame', 'StatTile', 'StatusDot']
    },
    {
      sourcePath: "src/pages/participant-dashboard/participant-sessions-section.tsx",
      components: ['CollectionPagination', 'CollectionToolbar', 'DataTableV2', 'IconCalendar']
    },
    {
      sourcePath: "src/pages/participant-dashboard/participant-assignments-section.tsx",
      components: ['CollectionPagination', 'CollectionToolbar', 'DataTableV2', 'IconDoc']
    }
  ],
  companies: [
    {
      sourcePath: "src/pages/companies/companies-page.tsx",
      components: ['Button', 'CollectionToolbar', 'IconUsers', 'ProTable']
    }
  ],
  "company-detail": [
    {
      sourcePath: "src/pages/companies/company-detail-page.tsx",
      components: ['Badge', 'Button', 'DetailSurface']
    }
  ],
  programs: [
    {
      sourcePath: "src/pages/programs/programs-page.tsx",
      components: ['Button', 'CollectionToolbar', 'IconDoc', 'PageFrame', 'ProTable']
    }
  ],
  "program-detail": [
    {
      sourcePath: "src/pages/programs/program-detail-page.tsx",
      components: ['Badge', 'Button', 'DetailSurface']
    }
  ],
  cohorts: [{ sourcePath: "src/pages/cohorts/cohorts-page.tsx",
      components: ['Button', 'CollectionToolbar', 'IconUsers', 'PageFrame', 'ProTable'] }],
  "my-cohorts": [{ sourcePath: "src/pages/cohorts/my-cohorts-page.tsx",
      components: ['CollectionToolbar', 'PageFrame', 'ProTable'] }],
  "cohort-create": [{ sourcePath: "src/pages/cohorts/cohorts-page.tsx",
      components: ['Button', 'CollectionToolbar', 'IconUsers', 'PageFrame', 'ProTable'] }],
  "cohort-workspace": [
    {
      sourcePath: "src/pages/cohorts/cohort-detail-page.tsx",
      components: ['Badge', 'Button', 'Module', 'ModuleBody', 'ModuleHead', 'PageFrame', 'Tabs']
    },
    {
      sourcePath: "src/pages/cohorts/cohort-detail/cohort-participants-tab.tsx",
      components: ['Alert', 'Badge', 'Button', 'CollectionPagination', 'CollectionToolbar', 'DataTableV2']
    },
    {
      sourcePath: "src/pages/cohorts/cohort-detail/cohort-sessions-tab.tsx",
      components: ['CollectionPagination', 'CollectionToolbar', 'DataTableV2']
    },
    {
      sourcePath: "src/pages/cohorts/cohort-detail/cohort-attendance-tab.tsx",
      components: ['CollectionPagination', 'CollectionToolbar', 'DataTableV2']
    },
    {
      sourcePath: "src/pages/cohorts/cohort-detail/cohort-progress-tab.tsx",
      components: ['CollectionPagination', 'CollectionToolbar', 'DataTableV2']
    },
    {
      sourcePath: "src/pages/cohorts/cohort-detail/cohort-invites-tab.tsx",
      components: ['CollectionPagination', 'CollectionToolbar', 'DataTableV2']
    },
    {
      sourcePath: "src/pages/cohorts/cohort-detail/cohort-recordings-tab.tsx",
      components: ['CollectionPagination', 'CollectionToolbar', 'DataTableV2']
    },
    {
      sourcePath: "src/pages/cohorts/cohort-detail/cohort-assignments-tab.tsx",
      components: ['CollectionPagination', 'CollectionToolbar', 'DataTableV2']
    }
  ],
  participants: [
    { sourcePath: "src/pages/participants/participants-page.tsx",
      components: ['Button', 'CollectionToolbar', 'IconUsers', 'PageFrame', 'ProTable'] }
  ],
  "participant-detail": [
    {
      sourcePath: "src/pages/participants/participant-detail-page.tsx",
      components: ['Badge', 'Button', 'DetailSurface']
    }
  ],
  "cohort-bulk-participants": [
    { sourcePath: "src/pages/participants/bulk-import/bulk-import-page.tsx",
      components: ['Button', 'PageFrame'] },
    {
      sourcePath: "src/pages/participants/bulk-import/bulk-import-modal.tsx",
      components: ['Alert', 'Badge', 'Button', 'Card', 'CollectionPagination', 'DataTableV2', 'Modal']
    }
  ],
  meetings: [{ sourcePath: "src/pages/sessions/sessions-page.tsx",
      components: ['Button', 'CollectionToolbar', 'IconCalendar', 'PageFrame', 'ProTable'] }],
  sessions: [{ sourcePath: "src/pages/sessions/sessions-page.tsx",
      components: ['Button', 'CollectionToolbar', 'IconCalendar', 'PageFrame', 'ProTable'] }],
  calendar: [
    {
      sourcePath: "src/pages/sessions/calendar-page.tsx",
      components: ['Badge', 'Card', 'IconCalendar', 'PageFrame']
    }
  ],
  "cohort-session-series": [
    {
      sourcePath: "src/pages/sessions/create-series-page.tsx",
      components: ['Alert', 'Button', 'Input', 'Module', 'ModuleBody', 'ModuleHead', 'PageFrame']
    }
  ],
  "clear-series": [
    {
      sourcePath: "src/pages/sessions/clear-series-page.tsx",
      components: ['Alert', 'Button', 'Input', 'Module', 'ModuleBody', 'ModuleHead', 'PageFrame']
    }
  ],
  "session-detail": [
    {
      sourcePath: "src/pages/sessions/session-detail-page.tsx",
      components: ['Badge', 'Button', 'DetailSurface', 'Module', 'ModuleBody', 'ModuleHead']
    }
  ],
  "session-invites": [
    { sourcePath: "src/pages/product-session-invites-surfaces.tsx", components: sessionInvitesComponents }
  ],
  "session-reschedule": [
    { sourcePath: "src/pages/product-session-reschedule-surfaces.tsx", components: sessionRescheduleComponents }
  ],
  reports: [
    { sourcePath: "src/pages/reports/reports-page.tsx",
      components: ['CollectionToolbar', 'IconDoc', 'PageFrame', 'ProTable'] },
    { sourcePath: "src/pages/reports/reports-columns.tsx",
      components: ['Badge', 'StatusDot'] }
  ],
  "org-admin-home": [
    {
      sourcePath: "src/pages/product-org-admin-surfaces.tsx",
      components: ['Alert', 'Button', 'Donut', 'Funnel', 'Meter', 'Module', 'ModuleBody', 'ModuleHead', 'PageFrame', 'ProTable', 'Sparkbars', 'StatTile', 'StatusDot', 'TrendChart']
    }
  ],
  organizations: [
    {
      sourcePath: "src/pages/product-platform-organizations-surface.tsx",
      components: ['Alert', 'Badge', 'Button', 'Card', 'DataTable', 'FormField', 'FormSurface', 'Input', 'Module', 'ModuleBody', 'ModuleGrid', 'ModuleHead']
    }
  ],
  "org-reports": [
    {
      sourcePath: "src/pages/product-org-reports-surfaces.tsx",
      components: ['Alert', 'Badge', 'Button', 'DataTableCollection', 'DetailSurface', 'StatusDot']
    }
  ],
  "report-detail": [
    {
      sourcePath: "src/pages/reports/report-detail-page.tsx",
      components: ['Badge', 'DetailSurface', 'Module', 'ModuleBody', 'ModuleHead']
    },
    {
      sourcePath: "src/pages/reports/report-detail/report-review-section.tsx",
      components: ['Alert', 'Badge', 'Button', 'Module', 'ModuleBody', 'ModuleHead']
    }
  ],
  "session-report": [
    {
      sourcePath: "src/pages/reports/session-report-page.tsx",
      components: ['Badge', 'DetailSurface', 'Module', 'ModuleBody', 'ModuleHead']
    },
    {
      sourcePath: "src/pages/reports/report-detail/report-review-section.tsx",
      components: ['Alert', 'Badge', 'Button', 'Module', 'ModuleBody', 'ModuleHead']
    }
  ],
  "transcript-detail": [
    {
      sourcePath: "src/pages/product-artifact-surfaces.tsx",
      components: ['Alert', 'Badge', 'Button', 'DetailSurface']
    }
  ],
  resources: [
    { sourcePath: "src/pages/resources/resources-page.tsx",
      components: ['Button', 'CollectionToolbar', 'IconDoc', 'PageFrame', 'ProTable'] },
    { sourcePath: "src/pages/resources/resources-columns.tsx",
      components: ['Badge', 'StatusDot'] }
  ],
  "resource-detail": [
    { sourcePath: "src/pages/resources/resource-detail-page.tsx",
      components: ['Badge', 'DetailSurface'] },
    {
      sourcePath: "src/pages/resources/resource-detail/resource-manage-section.tsx",
      components: ['Alert', 'Button', 'Module', 'ModuleBody', 'ModuleHead']
    },
    {
      sourcePath: "src/pages/resources/resource-detail/resource-download-section.tsx",
      components: ['Alert', 'Button', 'Module', 'ModuleBody', 'ModuleHead']
    }
  ],
  capstone: [
    { sourcePath: "src/pages/capstone/capstone-page.tsx",
      components: ['Alert', 'Button', 'DataTableCollection', 'IconChart', 'Module', 'ModuleBody', 'ModuleHead', 'PageFrame'] },
    { sourcePath: "src/pages/capstone/capstone-columns.tsx",
      components: ['Badge', 'StatusDot'] },
    { sourcePath: "src/pages/capstone/capstone-submissions-section.tsx",
      components: ['CollectionPagination', 'CollectionToolbar', 'DataTableV2', 'Module', 'ModuleBody', 'ModuleHead'] }
  ],
  "capstone-submissions": [
    { sourcePath: "src/pages/capstone/capstone-page.tsx",
      components: ['Alert', 'Button', 'DataTableCollection', 'IconChart', 'Module', 'ModuleBody', 'ModuleHead', 'PageFrame'] },
    { sourcePath: "src/pages/capstone/capstone-columns.tsx",
      components: ['Badge', 'StatusDot'] },
    { sourcePath: "src/pages/capstone/capstone-submissions-section.tsx",
      components: ['CollectionPagination', 'CollectionToolbar', 'DataTableV2', 'Module', 'ModuleBody', 'ModuleHead'] }
  ],
  certificates: [
    { sourcePath: "src/pages/certificates/certificates-page.tsx",
      components: ['CollectionToolbar', 'PageFrame', 'ProTable'] },
    { sourcePath: "src/pages/certificates/certificates-columns.tsx",
      components: ['Badge'] },
    { sourcePath: "src/pages/certificates/certificate-issue-section.tsx",
      components: ['Alert', 'Button', 'Input', 'Module', 'ModuleBody', 'ModuleHead'] }
  ],
  history: [
    {
      sourcePath: "src/pages/logs/logs-page.tsx",
      components: ['Alert', 'Badge', 'CollectionToolbar', 'ProTable']
    },
    {
      sourcePath: "src/pages/logs/logs-columns.tsx",
      components: ['Badge', 'StatusDot']
    }
  ],
  users: [{ sourcePath: "src/pages/users/users-page.tsx",
      components: ['Button', 'CollectionToolbar', 'IconUsers', 'ProTable', 'Switch'] }],
  "user-detail": [
    {
      sourcePath: "src/pages/users/user-detail-page.tsx",
      components: ['Badge', 'Button', 'DetailSurface', 'StatusDot']
    }
  ],
  "user-edit": [
    {
      sourcePath: "src/pages/users/user-edit-page.tsx",
      components: ['Button', 'FormSurface']
    }
  ],
  permissions: [
    {
      sourcePath: "src/pages/product-contract-guardrails.tsx",
      components: ['Alert', 'Badge', 'DataTableCollection', 'Module', 'ModuleBody', 'ModuleHead', 'ProTable']
    }
  ],
  exports: [
    {
      sourcePath: "src/pages/exports/exports-page.tsx",
      components: ['Alert', 'Badge', 'Button', 'Module', 'ModuleBody', 'ModuleHead', 'PageFrame', 'ProTable', 'Select']
    }
  ],
  settings: [{ sourcePath: "src/pages/product-profile-surfaces.tsx", components: profileComponents }],
  "settings-thresholds": [
    {
      sourcePath: "src/pages/settings/thresholds-page.tsx",
      components: ['Alert', 'Badge', 'FormSurface', 'IconCog']
    },
    {
      sourcePath: "src/pages/settings/threshold-editor-row.tsx",
      components: ['Alert', 'Badge', 'Button', 'Input']
    }
  ],
  tools: [
    {
      sourcePath: "src/pages/tools/tool-view-page.tsx",
      components: ['Badge', 'Module', 'ModuleBody', 'ModuleHead', 'PageFrame']
    }
  ],
  profile: [{ sourcePath: "src/pages/product-profile-surfaces.tsx", components: profileComponents }]
};

export const screenRenderAnchors: Record<string, string[]> = {
  login: ["LoginOtpSurface"],
  dashboard: ["dashboardScreen", "DashboardSurface"],
  companies: ["CompaniesPage"],
  "company-detail": ["CompanyDetailPage"],
  programs: ["ProgramsPage"],
  "program-detail": ["ProgramDetailPage"],
  cohorts: ["CohortsPage"],
  "my-cohorts": ["MyCohortsPage"],
  "cohort-create": ["CohortsPage"],
  "cohort-workspace": ["CohortDetailPage"],
  participants: ["ParticipantsPage"],
  "participant-detail": ["ParticipantDetailPage"],
  "cohort-bulk-participants": ["BulkImportPage"],
  meetings: ["SessionsPage"],
  sessions: ["SessionsPage"],
  calendar: ["CalendarPage"],
  "cohort-session-series": ["CreateSeriesPage"],
  "clear-series": ["ClearSeriesPage"],
  "session-detail": ["SessionDetailPage"],
  "session-invites": ["sessionInvitesScreen", "InviteOperationsSurface"],
  "session-reschedule": ["rescheduleScreen", "RescheduleSurface"],
  reports: ["ReportsPage"],
  "org-admin-home": ["orgAdminHomeScreen", "OrgAdminHomeSurface"],
  "org-reports": ["orgReportsScreen", "OrgReportsSurface"],
  organizations: ["PlatformOrganizationsSurface", "organizationColumns"],
  "report-detail": ["ReportDetailPage"],
  "session-report": ["SessionReportPage"],
  "transcript-detail": ["transcriptDetailScreen", "TranscriptDetailSurface"],
  resources: ["ResourcesPage"],
  "resource-detail": ["ResourceDetailPage"],
  capstone: ["CapstonePage"],
  "capstone-submissions": ["CapstonePage"],
  certificates: ["CertificatesPage"],
  history: ["LogsPage"],
  users: ["UsersPage"],
  "user-detail": ["UserDetailPage"],
  "user-edit": ["UserEditPage"],
  permissions: ["backendBlockedScreen", "Feature gate"],
  exports: ["ExportsPage"],
  settings: ["profileSettingsScreen", "ProfileSettingsSurface"],
  "settings-thresholds": ["ThresholdsPage"],
  tools: ["ToolViewPage"],
  profile: ["profileSettingsScreen", "ProfileSettingsSurface"]
};
